import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Le tissu est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'cells',
  family: 'shader',
  danceability: 0.84,
  requiresTier: 0,
  energyLevel: 0.80,
  bpmMin: 115,
  bpmMax: 175,
  movement: 0.80,
  tonality: 0.45,
  enabled: true
};

// Cells : un tissu de cellules de Voronoï aux parois de néon, qui se
// DIVISENT sur les temps (mitose) et FUSIONNENT sur les mesures. LOOK VDJ
// party (lib/vdjLook.js). Psytrance, techno, acid, bass music.
//
// UN SEUL QUAD PLEIN ÉCRAN. Pour chaque pixel :
//   p   position en unités de hauteur (hauteur 1), centrée sur la SCÈNE (R19)
//   pc  caméra : à-coup de mesure (zoom / roulis), kaléidoscope une phrase
//       sur deux (pli en coin, centré sur la scène)
//   X   la déformation de la phrase : FLAT, FISHEYE, POLAR (angle, rayon :
//       les cellules deviennent des secteurs qui tournent), WARP
//   le Voronoï dans X : les DEUX germes les plus proches, par une GRILLE
//   D'ACCÉLÉRATION (germes rangés par case sur le CPU, 4 par case, 2 par
//   texel ; le pixel teste les 9 cases voisines = 18 lectures, quel que soit
//   le nombre de cellules : la taille des cases suit le nombre de germes).
//
// LA PAROI (R1) : distance EXACTE à la médiatrice des deux germes
// w = (d2² - d1²) / (2 |p2 - p1|), en unités de X ; à l'écran sa pente est
// |J^T n| (n la normale de la médiatrice, J la jacobienne pixel -> X par
// différences finies de la déformation, comme Truchet) : dPx = w / |J^T n|.
// Cœur blanc 2 px, gaine de couleur, halo dessiné (pas de bloom : sur un
// cadre couvert de traits fins, le bloom partagé fait un voile uniforme).
//
// LA MITOSE : le germe se dédouble en deux germes confondus qui s'écartent
// en un demi-temps (ease-out) ; la médiatrice des deux EST la nouvelle paroi
// - elle naît au milieu de la cellule, s'allonge et pousse les voisines,
// sans rien d'autre que le Voronoï. Les deux germes portent un code de paire
// (id + phase) : leur paroi commune flashe en blanc, et leurs noyaux sont
// unis par un minimum lisse dont le rayon de raccord décroît - un haltère
// qui s'étire et se sépare. La FUSION est l'inverse : deux voisins se
// rejoignent en un temps, leur paroi s'efface, les noyaux fondent.
//
// Le CPU (≤ 256 germes) : dérive (rotationnel d'un bruit lent), répulsion
// douce (le tissu reste régulier), naissances, fusions ; une texture
// flottante 64 x 32 réécrite par frame (16 Ko). Le GPU fait tout le rendu.
//
// Debug : ?cellscount=<n> (cible fixe), ?cellsdivide=1 (8 divisions par
// temps), ?cellswarp=flat|fisheye|polar|warp, ?cellsmirror=0|1.

const TEX_W = 32, TEX_H = 32;      // addresses (texel x 2 + slot) < 2048: exact in half floats
const MAX_SEEDS = 256;
const SLOTS = 4;                    // seeds per grid cell
const BOX_MARGIN = 0.09;            // around the visible area (the warp, the fisheye)
const GRID_K = 1.25;                // grid cell = 1.25 x the mean spacing
// search: the Voronoi search's resolution divisor (the walls stay full-res)
const TIER = {
  0: { min: 24, max: 48, cap: 48, search: 4 },
  1: { min: 40, max: 120, cap: MAX_SEEDS, search: 6 },
  2: { min: 40, max: 120, cap: MAX_SEEDS, search: 2 }
};
export const WARPS = ['flat', 'fisheye', 'polar', 'warp'];
const WARP_POLAR = 2;
const DRIFT = 0.018;                // units of height / s
const REPEL = 2.0;                  // x spacing per s, at contact: strong enough to undo the drift's clumping
const REPEL_RANGE = 1.3;            // x the mean spacing
const NUCLEUS = 0.011;              // nucleus radius, X units
const DROP_FLASH_S = 0.0;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const NO_BPM = 124;
const SAFE_FLOOR = 0.5;
const LUM_W = 64, LUM_H = 36;
const ACCENT_FALLBACK = [0xff2bd6, 0x2bffea, 0xffc21a];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function easeOut(t) { const e = 1 - clamp01(t); return 1 - e * e * e; }
function easeInOut(t) { t = clamp01(t); return t * t * (3 - 2 * t); }

const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const SEARCH_GLSL = `

  precision highp float;
  uniform sampler2D uPos;          // 2 seeds per texel: x1 y1 x2 y2
  uniform sampler2D uMeta;         // hue1 code1 hue2 code2
  uniform vec2 uTex;
  uniform vec2 uGrid;              // cols, rows
  uniform vec4 uBox;               // x0, y0, cell w, cell h
  uniform float uPeriod;           // polar: the x period (the box width); 0 otherwise
  uniform float uAspect;
  uniform float uPx;               // a pixel, in height units
  uniform float uRoll;
  uniform vec3 uRollCS;           // cos, sin of the roll, 1 / the kick's zoom (on the CPU)
  uniform float uKickZoom;
  uniform float uWedges;
  uniform float uWarp;
  uniform float uSpin;
  uniform vec2 uWarpT;
  uniform float uFlow;
  uniform vec3 uHue;
  uniform float uPulse;
  uniform float uExposure;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform vec2 uStageCenter;
  uniform sampler2D uIds;          // the search pass: the three nearest seeds' addresses
  uniform float uSearchDiv;
  uniform float uDeriv;
  varying vec2 vUv;

  const float TAU = 6.28318531;

  vec2 rot(vec2 v, float a) { float c = cos(a), s = sin(a); return vec2(c * v.x - s * v.y, s * v.x + c * v.y); }
  vec2 fold(vec2 p, float wedges) {
    float r = length(p);
    float w = TAU / wedges;
    float a = mod(atan(p.y, p.x), 2.0 * w);
    a = abs(a - w);
    return vec2(cos(a), sin(a)) * r;
  }
  // p (height units, centred on the stage) -> X, where the seeds live
  vec2 warp(vec2 p) {
    // FLAT first and free: no root, no transcendental (the Pi pays for each)
    if (uWarp < 0.5) return p;
    if (uWarp < 1.5) return p * (1.0 - 0.5 * exp(-dot(p, p) / 0.16));             // FISHEYE (monotone)
    if (uWarp < 2.5)                                                                 // POLAR: angle over one period, radius
      return vec2(fract((atan(p.y, p.x) + uSpin) / TAU + 0.5) * uPeriod + uBox.x, length(p) - 0.5);
    return p + 0.05 * vec2(sin(p.y * 7.0 + uWarpT.x), sin(p.x * 7.0 + uWarpT.y));   // WARP: 0.05 < half of 0.9
  }
  // the cosine palette of the cover's three hues (Truchet's): the HUE is
  // interpolated, never the RGB; saturation full, luminance capped at 0.7
  vec3 palette(float s) {
    vec3 w = 0.5 + 0.5 * cos(TAU * (s - vec3(0.0, 1.0, 2.0) / 3.0));
    w *= w * w;
    vec2 v = w.x * vec2(cos(TAU * uHue.x), sin(TAU * uHue.x))
           + w.y * vec2(cos(TAU * uHue.y), sin(TAU * uHue.y))
           + w.z * vec2(cos(TAU * uHue.z), sin(TAU * uHue.z));
    float h = atan(v.y, v.x) / TAU;
    vec3 c = clamp(abs(mod(h * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
    float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
    return c * min(1.0, 0.7 / max(lum, 1e-3));
  }
  float smin(float a, float b, float k) {
    if (k <= 1e-6) return min(a, b);
    float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
    return mix(b, a, h) - k * h * (1.0 - h);
  }
  float safeAtt(vec2 uv) {
    vec2 s = (uv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  // the THREE nearest seeds: the wall is the nearer of the bisectors with
  // the second and the third (with the second alone, the distance jumps
  // where the second changes - creases inside the cells, wrong walls at the
  // junctions). A seed's ADDRESS: (texel index) x 2 + slot, < 2048 - exact
  // in the half-float target of the search pass.
  float d1, d2, d3;
  vec2 p1, p2, p3;
  float a1, a2, a3;
  void consider(vec2 X, vec2 s, float addr) {
    if (s.x > 9000.0) return;
    vec2 dv = X - s;
    if (uPeriod > 0.0) dv.x -= uPeriod * floor(dv.x / uPeriod + 0.5);
    float dd = dot(dv, dv);
    if (dd < d1) { d3 = d2; p3 = p2; a3 = a2; d2 = d1; p2 = p1; a2 = a1; d1 = dd; p1 = X - dv; a1 = addr; }
    else if (dd < d2) { d3 = d2; p3 = p2; a3 = a2; d2 = dd; p2 = X - dv; a2 = addr; }
    else if (dd < d3) { d3 = dd; p3 = X - dv; a3 = addr; }
  }
  vec3 addrUv(float a) {
    float slot = a - 2.0 * floor((a + 0.5) / 2.0);
    float ti = floor((a + 0.5) / 2.0);
    float ty = floor((ti + 0.5) / uTex.x);
    float tx = ti - ty * uTex.x;
    return vec3((tx + 0.5) / uTex.x, (ty + 0.5) / uTex.y, slot);
  }
  vec2 seedAt(float a) {
    vec3 u = addrUv(a);
    vec4 t = texture2D(uPos, u.xy);
    return u.z < 0.5 ? t.xy : t.zw;
  }
  void mapping(out vec2 X, out vec2 dX, out vec2 dY) {
    vec2 p = 0.5 * ((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uStageCenter);
    vec2 pc = vec2(uRollCS.x * p.x - uRollCS.y * p.y, uRollCS.y * p.x + uRollCS.x * p.y) * uRollCS.z;
    float h = uPx / uKickZoom;
    if (uWedges > 0.5) pc = fold(pc, uWedges);
    X = warp(pc);
    dX = warp(pc + vec2(h, 0.0)) - X;
    dY = warp(pc + vec2(0.0, h)) - X;
    if (uPeriod > 0.0) {
      dX.x -= uPeriod * floor(dX.x / uPeriod + 0.5);
      dY.x -= uPeriod * floor(dY.x / uPeriod + 0.5);
    }
  }
  void reset() { d1 = 1e9; d2 = 1e9; d3 = 1e9; p1 = vec2(0.0); p2 = vec2(1.0, 0.0); p3 = vec2(0.0, 1.0); a1 = -1.0; a2 = -1.0; a3 = -1.0; }

  // THE SEARCH, at a third of the resolution: the three nearest seeds of the
  // grid's 3 x 3 cells (5 x 5 where it is sparse), written as addresses
  void main() {
    vec2 X, dX, dY;
    mapping(X, dX, dY);
    reset();
    vec2 g = floor((X - uBox.xy) / uBox.zw);
    {
      for (int j = -1; j <= 1; j++) {
        float cy = g.y + float(j);
        if (cy < 0.0 || cy > uGrid.y - 0.5) continue;
        for (int i = -1; i <= 1; i++) {
          
          float cx = g.x + float(i);
          if (uPeriod > 0.0) cx = mod(cx, uGrid.x);
          else if (cx < 0.0 || cx > uGrid.x - 0.5) continue;
          for (int k = 0; k < 2; k++) {
            float ti = cy * uTex.x + cx * 2.0 + float(k);
            vec4 s = texture2D(uPos, vec2((cx * 2.0 + float(k) + 0.5) / uTex.x, (cy + 0.5) / uTex.y));
            consider(X, s.xy, ti * 2.0);
            consider(X, s.zw, ti * 2.0 + 1.0);
          }
        }
      }
    }
    float reach = min(uBox.z, uBox.w);
    if (d3 > reach * reach) {
      for (int j = -2; j <= 2; j++) {
        float cy = g.y + float(j);
        if (cy < 0.0 || cy > uGrid.y - 0.5) continue;
        for (int i = -2; i <= 2; i++) {
          if (abs(float(i)) < 1.5 && abs(float(j)) < 1.5) continue;
          float cx = g.x + float(i);
          if (uPeriod > 0.0) cx = mod(cx, uGrid.x);
          else if (cx < 0.0 || cx > uGrid.x - 0.5) continue;
          for (int k = 0; k < 2; k++) {
            float ti = cy * uTex.x + cx * 2.0 + float(k);
            vec4 s = texture2D(uPos, vec2((cx * 2.0 + float(k) + 0.5) / uTex.x, (cy + 0.5) / uTex.y));
            consider(X, s.xy, ti * 2.0);
            consider(X, s.zw, ti * 2.0 + 1.0);
          }
        }
      }
    }
    // the third seed matters only near a junction: where its bisector could
    // be nearer than the second's somewhere in this texel. Elsewhere, -1: the
    // full pass skips its fetch and its wall (a coherent branch: whole regions)
    if (d3 < 1e8) {
      vec2 dXs = dFdx(X), dYs = dFdy(X);
      if (uPeriod > 0.0) { dXs.x -= uPeriod * floor(dXs.x / uPeriod + 0.5); dYs.x -= uPeriod * floor(dYs.x / uPeriod + 0.5); }
      float reach = 1.5 * (length(dXs) + length(dYs));       // a texel and a half, in X units
      float w2 = (d2 - d1) / (2.0 * max(length(p2 - p1), 1e-6));
      float w3 = (d3 - d1) / (2.0 * max(length(p3 - p1), 1e-6));
      if (w3 - w2 > reach) a3 = -1.0;
    }
    gl_FragColor = vec4(a1, a2, a3, 1.0);
  }
`;
const fragmentShader = `

  precision highp float;
  uniform sampler2D uPos;          // 2 seeds per texel: x1 y1 x2 y2
  uniform sampler2D uMeta;         // hue1 code1 hue2 code2
  uniform vec2 uTex;
  uniform vec2 uGrid;              // cols, rows
  uniform vec4 uBox;               // x0, y0, cell w, cell h
  uniform float uPeriod;           // polar: the x period (the box width); 0 otherwise
  uniform float uAspect;
  uniform float uPx;               // a pixel, in height units
  uniform float uRoll;
  uniform vec3 uRollCS;           // cos, sin of the roll, 1 / the kick's zoom (on the CPU)
  uniform float uKickZoom;
  uniform float uWedges;
  uniform float uWarp;
  uniform float uSpin;
  uniform vec2 uWarpT;
  uniform float uFlow;
  uniform vec3 uHue;
  uniform float uPulse;
  uniform float uExposure;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform vec2 uStageCenter;
  uniform sampler2D uIds;          // the search pass: the three nearest seeds' addresses
  uniform float uSearchDiv;
  uniform float uDeriv;
  uniform sampler2D uCol;          // each seed's colour (the palette, on the CPU), by address
  varying vec2 vUv;

  const float TAU = 6.28318531;

  vec2 rot(vec2 v, float a) { float c = cos(a), s = sin(a); return vec2(c * v.x - s * v.y, s * v.x + c * v.y); }
  vec2 fold(vec2 p, float wedges) {
    float r = length(p);
    float w = TAU / wedges;
    float a = mod(atan(p.y, p.x), 2.0 * w);
    a = abs(a - w);
    return vec2(cos(a), sin(a)) * r;
  }
  // p (height units, centred on the stage) -> X, where the seeds live
  vec2 warp(vec2 p) {
    // FLAT first and free: no root, no transcendental (the Pi pays for each)
    if (uWarp < 0.5) return p;
    if (uWarp < 1.5) return p * (1.0 - 0.5 * exp(-dot(p, p) / 0.16));             // FISHEYE (monotone)
    if (uWarp < 2.5)                                                                 // POLAR: angle over one period, radius
      return vec2(fract((atan(p.y, p.x) + uSpin) / TAU + 0.5) * uPeriod + uBox.x, length(p) - 0.5);
    return p + 0.05 * vec2(sin(p.y * 7.0 + uWarpT.x), sin(p.x * 7.0 + uWarpT.y));   // WARP: 0.05 < half of 0.9
  }
  // the cosine palette of the cover's three hues (Truchet's): the HUE is
  // interpolated, never the RGB; saturation full, luminance capped at 0.7
  vec3 palette(float s) {
    vec3 w = 0.5 + 0.5 * cos(TAU * (s - vec3(0.0, 1.0, 2.0) / 3.0));
    w *= w * w;
    vec2 v = w.x * vec2(cos(TAU * uHue.x), sin(TAU * uHue.x))
           + w.y * vec2(cos(TAU * uHue.y), sin(TAU * uHue.y))
           + w.z * vec2(cos(TAU * uHue.z), sin(TAU * uHue.z));
    float h = atan(v.y, v.x) / TAU;
    vec3 c = clamp(abs(mod(h * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
    float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
    return c * min(1.0, 0.7 / max(lum, 1e-3));
  }
  float smin(float a, float b, float k) {
    if (k <= 1e-6) return min(a, b);
    float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
    return mix(b, a, h) - k * h * (1.0 - h);
  }
  float safeAtt(vec2 uv) {
    vec2 s = (uv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  // the THREE nearest seeds: the wall is the nearer of the bisectors with
  // the second and the third (with the second alone, the distance jumps
  // where the second changes - creases inside the cells, wrong walls at the
  // junctions). A seed's ADDRESS: (texel index) x 2 + slot, < 2048 - exact
  // in the half-float target of the search pass.
  float d1, d2, d3;
  vec2 p1, p2, p3;
  float a1, a2, a3;
  void consider(vec2 X, vec2 s, float addr) {
    if (s.x > 9000.0) return;
    vec2 dv = X - s;
    if (uPeriod > 0.0) dv.x -= uPeriod * floor(dv.x / uPeriod + 0.5);
    float dd = dot(dv, dv);
    if (dd < d1) { d3 = d2; p3 = p2; a3 = a2; d2 = d1; p2 = p1; a2 = a1; d1 = dd; p1 = X - dv; a1 = addr; }
    else if (dd < d2) { d3 = d2; p3 = p2; a3 = a2; d2 = dd; p2 = X - dv; a2 = addr; }
    else if (dd < d3) { d3 = dd; p3 = X - dv; a3 = addr; }
  }
  vec3 addrUv(float a) {
    float slot = a - 2.0 * floor((a + 0.5) / 2.0);
    float ti = floor((a + 0.5) / 2.0);
    float ty = floor((ti + 0.5) / uTex.x);
    float tx = ti - ty * uTex.x;
    return vec3((tx + 0.5) / uTex.x, (ty + 0.5) / uTex.y, slot);
  }
  vec2 seedAt(float a) {
    vec3 u = addrUv(a);
    vec4 t = texture2D(uPos, u.xy);
    return u.z < 0.5 ? t.xy : t.zw;
  }
  void mapping(out vec2 X, out vec2 dX, out vec2 dY) {
    vec2 p = 0.5 * ((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uStageCenter);
    vec2 pc = vec2(uRollCS.x * p.x - uRollCS.y * p.y, uRollCS.y * p.x + uRollCS.x * p.y) * uRollCS.z;
    float h = uPx / uKickZoom;
    if (uWedges > 0.5) pc = fold(pc, uWedges);
    X = warp(pc);
    dX = warp(pc + vec2(h, 0.0)) - X;
    dY = warp(pc + vec2(0.0, h)) - X;
    if (uPeriod > 0.0) {
      dX.x -= uPeriod * floor(dX.x / uPeriod + 0.5);
      dY.x -= uPeriod * floor(dY.x / uPeriod + 0.5);
    }
  }
  void reset() { d1 = 1e9; d2 = 1e9; d3 = 1e9; p1 = vec2(0.0); p2 = vec2(1.0, 0.0); p3 = vec2(0.0, 1.0); a1 = -1.0; a2 = -1.0; a3 = -1.0; }

  uniform sampler2D uSeed;         // per address: x, y, pair code
  float q1, q2, q3;
  vec2 seedUv(float a) { float ty = floor((a + 0.5) / 64.0); return vec2((a - ty * 64.0 + 0.5) / 64.0, (ty + 0.5) / 32.0); }
  void considerC(vec2 X, vec4 s, float addr) {
    vec2 dv = X - s.xy;
    if (uPeriod > 0.0) dv.x -= uPeriod * floor(dv.x / uPeriod + 0.5);
    float dd = dot(dv, dv);
    if (dd < d1) { d3 = d2; p3 = p2; q3 = q2; d2 = d1; p2 = p1; q2 = q1; d1 = dd; p1 = X - dv; q1 = s.z; a1 = addr; }
    else if (dd < d2) { d3 = d2; p3 = p2; q3 = q2; d2 = dd; p2 = X - dv; q2 = s.z; }
    else if (dd < d3) { d3 = dd; p3 = X - dv; q3 = s.z; }
  }
  // THE FULL-RESOLUTION PASS: the three candidates of the search pass (one
  // texel, nearest), re-ranked at this pixel; walls and nuclei exact, in px
  void main() {
    // one warp; the jacobian from the hardware derivatives (2 warps fewer)
    vec2 p = 0.5 * ((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uStageCenter);
    vec2 pc = vec2(uRollCS.x * p.x - uRollCS.y * p.y, uRollCS.y * p.x + uRollCS.x * p.y) * uRollCS.z;
    if (uWedges > 0.5) pc = fold(pc, uWedges);
    vec2 X = warp(pc);
    // per FULL-frame pixel: the comfort measure renders this shader in 64 x 36
    vec2 dX = dFdx(X) * uDeriv, dY = dFdy(X) * uDeriv;
    if (uPeriod > 0.0) {
      dX.x -= uPeriod * floor(dX.x / uPeriod + 0.5);
      dY.x -= uPeriod * floor(dY.x / uPeriod + 0.5);
    }
    vec4 ids = texture2D(uIds, vUv);
    // the three candidates, ONE fetch each (position + pair code, by address)
    vec4 s1 = texture2D(uSeed, seedUv(ids.x)), s2 = texture2D(uSeed, seedUv(ids.y));
    reset();
    if (ids.x > -0.5) considerC(X, s1, ids.x);
    if (ids.y > -0.5) considerC(X, s2, ids.y);
    if (ids.z > -0.5) considerC(X, texture2D(uSeed, seedUv(ids.z)), ids.z);
    if (d2 > 1e8) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
    // the wall in px without a normalisation: (d2 - d1) / (2 |J^T e|)
    vec2 e = p2 - p1;
    float le = max(length(e), 1e-6);
    float w = max(0.0, (d2 - d1) / (2.0 * le));
    float dPx = max(0.0, d2 - d1) / max(2.0 * length(vec2(dot(dX, e), dot(dY, e))), 1e-9);
    float F1 = sqrt(d1), F2 = sqrt(d2);
    float scale = max(length(dX), 1e-7);                 // X units per pixel
    float code1 = q1, code2 = q2;
    // the pair: a division (+) or a fusion (-) between the two nearest seeds
    float b1 = abs(code1), b2 = abs(code2);
    bool pair = b1 >= 1.0 && floor(b1) == floor(b2) && code1 * code2 > 0.0;
    float phase = pair ? fract(b1) : 1.0;
    bool merging = pair && code1 < 0.0;
    // two dividing seeds still (almost) together have no wall yet
    float apart = pair && !merging ? smoothstep(2.0, 8.0, le / scale) : 1.0;
    if (apart < 0.01) { dPx = 1e9; w = 1e9; }
    // the wall with the THIRD seed, if nearer: a wall of another pair, plain
    bool third = false;
    if (d3 < 1e8) {
      vec2 e3 = p3 - p1;
      float px3 = max(0.0, d3 - d1) / max(2.0 * length(vec2(dot(dX, e3), dot(dY, e3))), 1e-9);
      if (px3 < dPx) { dPx = px3; w = max(0.0, (d3 - d1) / (2.0 * max(length(e3), 1e-6))); third = true; }
    }
    float wallA = third ? 1.0 : (merging ? 1.0 - phase : apart);
    float newWall = third || !pair || merging ? 0.0 : (1.0 - phase) * (1.0 - phase) * apart;
    // the cell's colour flows: the palette scrolls through the hues
    vec3 c1 = texture2D(uCol, seedUv(a1)).rgb;
    // inside: a very dark radial gradient, lighter toward the wall (lum <= 0.15)
    float rr = F1 / max(F1 + w, 1e-6);
    vec3 col = c1 * 0.16 * rr * rr;
    // the wall (R1): core 2 px, sheath, halo
    float core = 1.0 - smoothstep(0.6, 1.4, dPx);
    float sheath = 1.0 - smoothstep(2.4, 3.6, dPx);
    float halo = 0.16 * max(0.0, 1.0 - dPx / 12.0);      // linear, 0 at 12 px (the Pi: every op counts)
    col += c1 * (sheath * 0.85 * (1.0 + 0.10 * uPulse) + (1.0 - sheath) * halo) * wallA;
    col = mix(col, vec3(1.0), clamp(core * wallA + newWall * sheath * 0.8, 0.0, 1.0));
    // the nucleus: a disc; a dividing pair is a dumbbell (a smooth union
    // whose bridge thins as they separate), a fusing pair melts together
    float nd = F1 - ${NUCLEUS.toFixed(4)};
    if (pair) {
      // the bridge outlives the first, fast half of the separation: a dumbbell
      float k = merging ? ${NUCLEUS.toFixed(4)} * 2.5 * phase : min(0.12 * (1.0 - phase), 1.6 * le);   // grows with the gap: no swelling at t = 0
      nd = smin(nd, F2 - ${NUCLEUS.toFixed(4)}, k);
    }
    float nPx = nd / scale;
    float nuc = 1.0 - smoothstep(-0.8, 0.8, nPx);
    col = mix(col, mix(c1, vec3(1.0), 0.35) * 0.95, nuc);
    gl_FragColor = vec4(col * uExposure * safeAtt(vUv), 1.0);
  }
`;

let MAT = null;
let MAT_SEARCH = null;
let TEXS = null;
function textures() {
  if (TEXS) return TEXS;
  const mk = () => {
    const t = new THREE.DataTexture(new Float32Array(TEX_W * TEX_H * 4), TEX_W, TEX_H, THREE.RGBAFormat, THREE.FloatType);
    t.minFilter = t.magFilter = THREE.NearestFilter; t.generateMipmaps = false;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.needsUpdate = true;
    return t;
  };
  TEXS = { pos: mk(), meta: mk(), seed: (() => { const t = new THREE.DataTexture(new Float32Array(64 * 32 * 4), 64, 32, THREE.RGBAFormat, THREE.FloatType); t.minFilter = t.magFilter = THREE.NearestFilter; t.generateMipmaps = false; t.needsUpdate = true; return t; })(), col: (() => { const t = new THREE.DataTexture(new Uint8Array(64 * 32 * 4), 64, 32, THREE.RGBAFormat, THREE.UnsignedByteType); t.minFilter = t.magFilter = THREE.NearestFilter; t.generateMipmaps = false; t.needsUpdate = true; return t; })() };
  return TEXS;
}
function material() {
  if (MAT) return MAT;
  const T = textures();
  MAT = new THREE.ShaderMaterial({
    uniforms: {
      uPos: { value: T.pos }, uMeta: { value: T.meta }, uTex: { value: new THREE.Vector2(TEX_W, TEX_H) },
      uGrid: { value: new THREE.Vector2(1, 1) }, uBox: { value: new THREE.Vector4(-1, -0.6, 1, 1) }, uPeriod: { value: 0 },
      uAspect: safeZoneUniforms.uAspect, uPx: { value: 1 / 720 },
      uRoll: { value: 0 }, uRollCS: { value: new THREE.Vector3(1, 0, 1) }, uKickZoom: { value: 1 }, uWedges: { value: 0 }, uWarp: { value: 0 }, uSpin: { value: 0 },
      uWarpT: { value: new THREE.Vector2() }, uFlow: { value: 0 }, uHue: { value: new THREE.Vector3(0.88, 0.5, 0.12) },
      uPulse: { value: 0 }, uExposure: { value: 1 },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather,
      uStageCenter: safeZoneUniforms.uStageCenter, uIds: { value: null }, uCol: { value: T.col }, uSeed: { value: T.seed }, uSearchDiv: { value: 3 }, uDeriv: { value: 1 }
    },
    vertexShader: quadVertexShader, fragmentShader, depthTest: false, depthWrite: false
  });
  // the search pass shares the uniforms object
  MAT_SEARCH = new THREE.ShaderMaterial({ uniforms: MAT.uniforms, vertexShader: quadVertexShader, fragmentShader: SEARCH_GLSL, depthTest: false, depthWrite: false });
  return MAT;
}
// the GLSL palette, on the CPU: one colour per seed and frame instead of a
// palette per pixel (the Pi: the full-resolution pass was 25 ms)
const _rgb = [0, 0, 0];
function palette(s, hue, out) {
  const T = Math.PI * 2;
  let vx = 0, vy = 0;
  const hs = [hue.x, hue.y, hue.z];
  for (let i = 0; i < 3; i++) { let w = 0.5 + 0.5 * Math.cos(T * (s - i / 3)); w = w * w * w; vx += w * Math.cos(T * hs[i]); vy += w * Math.sin(T * hs[i]); }
  const h = Math.atan2(vy, vx) / T;
  const c = [0, 4, 2].map((o) => { const x = h * 6 + o; const m = x - 6 * Math.floor(x / 6); return Math.max(0, Math.min(1, Math.abs(m - 3) - 1)); });
  const lum = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  const k = Math.min(1, 0.7 / Math.max(lum, 1e-3));
  out[0] = c[0] * k; out[1] = c[1] * k; out[2] = c[2] * k;
  return out;
}
function hueOf(hex) { const hsl = { h: 0, s: 0, l: 0 }; new THREE.Color(hex).getHSL(hsl); return hsl.h; }
function hueDist(a, b) { const d = Math.abs(a - b) % 1; return Math.min(d, 1 - d); }

export class CellsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const n = parseInt(urlParam('cellscount'), 10);
    this._forceCount = Number.isFinite(n) ? Math.max(4, Math.min(this._layout.cap, n)) : null;
    this._forceDivide = urlParam('cellsdivide') === '1';
    const wp = WARPS.indexOf(urlParam('cellswarp'));
    this._forceWarp = wp >= 0 ? wp : null;
    const mi = urlParam('cellsmirror');
    this._forceMirror = mi === '1' ? 1 : mi === '0' ? 0 : null;
    // no bloom: the halo is drawn (R1); NoToneMapping keeps the neon saturated
    this._post = new VdjPost({ bloomStrength: 0, bloomThreshold: 0.9, grain: 0.012, vignette: 0.40, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    this._mat = material(); this._u = this._mat.uniforms;
    this._tex = textures();
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._beforeRender(renderer); };
    scene.add(this._display);
    // the search pass: the three nearest seeds' addresses, at 1/3 resolution,
    // half floats (integers exact to 2048), nearest (the Pi cannot filter floats)
    this._ids = new THREE.WebGLRenderTarget(4, 4, { type: THREE.HalfFloatType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    this._u.uIds.value = this._ids.texture;
    this._searchScene = new THREE.Scene();
    this._searchQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), MAT_SEARCH);
    this._searchQuad.frustumCulled = false;
    this._searchScene.add(this._searchQuad);
    // the comfort measure: the same shader in 64 x 36, with the full frame's pixel size
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_W, LUM_H, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(LUM_W * LUM_H * 4);
    this._lumScene = new THREE.Scene();
    this._lumQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mat);
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._lumAge = 0; this._lumMeasured = 0.12; this._lumPending = false;

    // state
    this._time = 0; this._beats = 0; this._lastBeats = null; this._beatPeriod = 60 / NO_BPM;
    this._lastBeatPhase = null; this._lastBeatIndex = null; this._lastBarPhase = null; this._lastPhrasePhase = null;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0; this._free = 0;
    this._energy = 0.6; this._build = 0; this._pulse = 0; this._flow = 0;
    this._warpMode = this._forceWarp != null ? this._forceWarp : 0; this._mirror = 0; this._palette = 0;
    this._spin = 0; this._kick = { zoom: 1, roll: 0 };
    this._reorgUntil = -1; this._reorgBars = 0; this._lastDropKey = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteSeen = undefined; this._paletteHex = null;
    this._nextId = 1; this._nextPair = 1;
    this._seed = 1234567;
    this._aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    this._setBox(this._aspect);
    this._seeds = [];
    const n0 = this._forceCount || Math.round((this._layout.min + this._layout.max) / 2);
    for (let i = 0; i < n0; i++) this._seeds.push(this._newSeed(this._x0 + this._rnd() * this._bw, this._y0 + this._rnd() * this._bh));
    for (let k = 0; k < 40; k++) this._relax(0.1);
    this._applyPalette();
    this._upload();
  }

  _rnd() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }
  _newSeed(x, y, hue) {
    return { id: this._nextId++, x, y, hue: hue != null ? hue : 0.45 * (x / Math.max(1, this._bw)) + 0.08 * this._rnd(), kind: 0, pair: 0, t0: 0, dur: 1 };
  }
  _setBox(aspect) {
    const old = this._bw ? { x0: this._x0, y0: this._y0, bw: this._bw, bh: this._bh } : null;
    this._bw = aspect + 2 * BOX_MARGIN; this._bh = 1 + 2 * BOX_MARGIN;
    this._x0 = -this._bw / 2; this._y0 = -this._bh / 2;
    if (old && this._seeds) for (const s of this._seeds) {
      s.x = this._x0 + (s.x - old.x0) / old.bw * this._bw; s.y = this._y0 + (s.y - old.y0) / old.bh * this._bh;
      if (s.cx != null) { s.cx = this._x0 + (s.cx - old.x0) / old.bw * this._bw; s.cy = this._y0 + (s.cy - old.y0) / old.bh * this._bh; }
    }
  }
  _spacing(n) { return Math.sqrt(this._bw * this._bh / Math.max(4, n)); }

  // --- the tissue: drift, repulsion, divisions, fusions ---------------------------
  _target() {
    const L = this._layout;
    if (this._forceCount) return this._forceCount;
    let t = L.min + (L.max - L.min) * clamp01((this._energy - 0.25) / 0.6);
    t *= 1 + 0.4 * this._build;                       // the build: more, smaller, nervous
    return Math.min(L.cap, Math.round(t));
  }
  _free_(s) { return s.kind === 0; }
  _divide(count) {
    const cap = this._layout.cap;
    const free = this._seeds.filter((s) => s.kind === 0);
    const sp = this._spacing(this._seeds.length);
    let done = 0;
    while (done < count && this._seeds.length < cap && free.length) {
      const s = free.splice(Math.floor(this._rnd() * free.length), 1)[0];
      const a = this._rnd() * Math.PI * 2;
      const pair = this._nextPair++;
      const b = this._newSeed(s.x, s.y, s.hue + 0.03);
      for (const q of [s, b]) { q.kind = 1; q.pair = pair; q.t0 = this._time; q.dur = 0.5 * this._beatPeriod; q.cx = s.x; q.cy = s.y; q.dx = Math.cos(a); q.dy = Math.sin(a); q.sep = 0.5 * sp * 0.85; }
      s.sign = 1; b.sign = -1;
      this._seeds.push(b);
      done++;
    }
    return done;
  }
  _merge(count) {
    const free = this._seeds.filter((s) => s.kind === 0);
    const sp = this._spacing(this._seeds.length);
    let done = 0;
    for (let tries = 0; tries < count * 8 && done < count && free.length > 1; tries++) {
      const i = Math.floor(this._rnd() * free.length);
      const a = free[i];
      if (a.kind !== 0) continue;
      let best = null, bd = 1.6 * sp;
      for (const b of free) { if (b === a || b.kind !== 0) continue; const d = Math.hypot(b.x - a.x, b.y - a.y); if (d < bd) { bd = d; best = b; } }
      // (a fusion needs a free neighbour: after a drop most are free again)
      if (!best) continue;
      const pair = this._nextPair++;
      for (const q of [a, best]) { q.kind = 2; q.pair = pair; q.t0 = this._time; q.dur = this._beatPeriod; q.sx = q.x; q.sy = q.y; }
      a.partner = best; best.partner = a; a.keeps = true; best.keeps = false;
      done++;
    }
    return done;
  }
  _drift(x, y, t) {
    // the rotational of a slow smooth noise (divergence-free). Only
    // wavelengths well under the frame's: a long one is a uniform wind that
    // piles the whole tissue against one side (10 seeds on the left, 23 on
    // the right, measured). Divided by k: a speed of ~1 x DRIFT.
    const k = 5.0, e = 0.002;
    const psi = (X, Y) => (Math.sin(X * k + t * 0.21) * Math.cos(Y * k * 1.3 - t * 0.17) + 0.5 * Math.sin(X * k * 0.94 + Y * k * 1.06 + t * 0.13)) / k;
    const dpdx = (psi(x + e, y) - psi(x - e, y)) / (2 * e), dpdy = (psi(x, y + e) - psi(x, y - e)) / (2 * e);
    return [dpdy, -dpdx];
  }
  _relax(dt) {
    const S = this._seeds, n = S.length, sp = this._spacing(n), polar = this._warpMode === WARP_POLAR;
    const R = REPEL_RANGE * sp;
    const fx = new Float32Array(n), fy = new Float32Array(n);
    // buckets of side R: each seed only meets the 3 x 3 buckets around it
    // (all pairs cost the Pi ~1 ms a frame at 120 seeds)
    const bc = Math.max(1, Math.floor(this._bw / R)), br = Math.max(1, Math.floor(this._bh / R));
    const bw = this._bw / bc, bh = this._bh / br;
    const head = new Int32Array(bc * br).fill(-1), next = new Int32Array(n), bx = new Int32Array(n), by = new Int32Array(n);
    for (let i = 0; i < n; i++) {
      bx[i] = Math.min(bc - 1, Math.max(0, Math.floor((S[i].x - this._x0) / bw)));
      by[i] = Math.min(br - 1, Math.max(0, Math.floor((S[i].y - this._y0) / bh)));
      const c = by[i] * bc + bx[i]; next[i] = head[c]; head[c] = i;
    }
    for (let i = 0; i < n; i++) {
      const a = S[i];
      for (let oy = -1; oy <= 1; oy++) {
        const yy = by[i] + oy; if (yy < 0 || yy >= br) continue;
        for (let ox = -1; ox <= 1; ox++) {
          let xx = bx[i] + ox;
          if (polar) xx = (xx + bc) % bc; else if (xx < 0 || xx >= bc) continue;
          for (let j = head[yy * bc + xx]; j >= 0; j = next[j]) {
            if (j <= i) continue;
            const b = S[j];
            if (a.pair && a.pair === b.pair) continue;
            let dx = b.x - a.x; const dy = b.y - a.y;
            if (polar) dx -= this._bw * Math.round(dx / this._bw);
            const d2 = dx * dx + dy * dy;
            if (d2 >= R * R || d2 < 1e-12) continue;
            const d = Math.sqrt(d2), f = (R - d) / R * REPEL * sp;
            fx[i] -= dx / d * f; fy[i] -= dy / d * f; fx[j] += dx / d * f; fy[j] += dy / d * f;
          }
        }
      }
    }
    // the box's edges push back too (the drift would pile seeds against them)
    for (let i = 0; i < n; i++) {
      const s = S[i], h = 0.5 * sp;
      const ey = Math.min(s.y - this._y0, this._y0 + this._bh - s.y);
      if (ey < h) fy[i] += (s.y - this._y0 < h ? 1 : -1) * (h - ey) / h * REPEL * sp;
      if (!polar) {
        const ex = Math.min(s.x - this._x0, this._x0 + this._bw - s.x);
        if (ex < h) fx[i] += (s.x - this._x0 < h ? 1 : -1) * (h - ex) / h * REPEL * sp;
      }
    }
    for (let i = 0; i < n; i++) {
      const s = S[i];
      if (s.kind === 1) { s.cx += fx[i] * dt; s.cy += fy[i] * dt; }
      else if (s.kind === 0) { s.x += fx[i] * dt; s.y += fy[i] * dt; }
    }
  }
  _step(dt) {
    const S = this._seeds, t = this._time, nervous = 1 + 1.2 * this._build;
    this._relax(dt);
    for (const s of S) {
      const px = s.kind === 1 ? s.cx : s.x, py = s.kind === 1 ? s.cy : s.y;
      const v = this._drift(px, py, t);
      const mv = DRIFT * nervous * dt;
      if (s.kind === 1) { s.cx += v[0] * mv; s.cy += v[1] * mv; }
      else if (s.kind === 0) { s.x += v[0] * mv; s.y += v[1] * mv; }
    }
    // divisions: the two seeds separate in half a beat (ease-out)
    const removed = new Set();
    for (const s of S) {
      if (s.kind === 1) {
        const ph = (t - s.t0) / s.dur;
        const e = easeOut(ph) * s.sep * s.sign;
        s.x = s.cx + s.dx * e; s.y = s.cy + s.dy * e;
        // the pair code stays for half a beat more: the new wall's flash, the dumbbell
        if (ph >= 2) { s.kind = 0; s.pair = 0; }
      } else if (s.kind === 2) {
        const ph = (t - s.t0) / s.dur, p = s.partner;
        const mx = 0.5 * (s.sx + p.sx), my = 0.5 * (s.sy + p.sy);
        const e = easeInOut(ph);
        s.x = s.sx + (mx - s.sx) * e; s.y = s.sy + (my - s.sy) * e;
        if (ph >= 1) { if (s.keeps) { s.kind = 0; s.pair = 0; s.x = mx; s.y = my; s.partner = null; } else removed.add(s); }
      }
    }
    if (removed.size) this._seeds = S.filter((s) => !removed.has(s));
    // the box: clamped (polar: x wraps)
    const polar = this._warpMode === WARP_POLAR;
    for (const s of this._seeds) {
      if (polar) { s.x = this._x0 + ((s.x - this._x0) % this._bw + this._bw) % this._bw; if (s.cx != null) s.cx = this._x0 + ((s.cx - this._x0) % this._bw + this._bw) % this._bw; }
      else s.x = Math.max(this._x0 + 1e-4, Math.min(this._x0 + this._bw - 1e-4, s.x));
      s.y = Math.max(this._y0 + 1e-4, Math.min(this._y0 + this._bh - 1e-4, s.y));
    }
  }

  // the grid: seeds binned per cell, 4 per cell, 2 per texel
  _upload() {
    const S = this._seeds, n = S.length;
    const pos = this._tex.pos.image.data, meta = this._tex.meta.image.data, col = this._tex.col.image.data, seedT = this._tex.seed.image.data, U = this._u;
    // the grid: as coarse as the seeds' spacing allows (the 3 x 3 search must
    // reach the third nearest seed), finer until no cell holds more than 4
    let c = GRID_K * this._spacing(n), cols, rows, cw, ch, cellOf, fill;
    for (let tries = 0; tries < 6; tries++) {
      cols = Math.max(1, Math.min(TEX_W / 2, Math.floor(this._bw / c)));
      rows = Math.max(1, Math.min(TEX_H, Math.floor(this._bh / c)));
      cw = this._bw / cols; ch = this._bh / rows;
      fill = new Uint8Array(cols * rows);
      cellOf = new Int32Array(n);
      let over = false;
      for (let i = 0; i < n; i++) {
        const s = S[i];
        const gx = Math.min(cols - 1, Math.max(0, Math.floor((s.x - this._x0) / cw)));
        const gy = Math.min(rows - 1, Math.max(0, Math.floor((s.y - this._y0) / ch)));
        cellOf[i] = gy * cols + gx;
        if (++fill[cellOf[i]] > SLOTS) over = true;
      }
      if (!over) break;
      c *= 0.88;
    }
    pos.fill(1e4); seedT.fill(0);
    fill.fill(0);
    let overflow = 0;
    for (let i = 0; i < n; i++) {
      const s = S[i];
      const cell = cellOf[i], gx = cell % cols, gy = (cell / cols) | 0;
      const k = fill[cell];
      if (k >= SLOTS) { overflow++; continue; }
      fill[cell] = k + 1;
      const texel = gy * TEX_W + gx * 2 + (k >> 1), o = texel * 4 + (k & 1) * 2;
      pos[o] = s.x; pos[o + 1] = s.y;
      let code = 0;
      if (s.pair) {
        // the phase runs over the separation itself (the dumbbell, the new wall's flash)
        const ph = s.kind === 1 || s.kind === 2 ? clamp01((this._time - s.t0) / s.dur) : 1;
        code = (s.pair % 4096 + 1) + Math.min(0.999, ph);
        if (s.kind === 2) code = -code;
      }
      // the colour, by address (texel x 2 + slot): the palette per seed, here
      const addr = texel * 2 + (k & 1);
      palette(s.hue + this._flow, U.uHue.value, _rgb);
      col[addr * 4] = _rgb[0] * 255; col[addr * 4 + 1] = _rgb[1] * 255; col[addr * 4 + 2] = _rgb[2] * 255;
      seedT[addr * 4] = s.x; seedT[addr * 4 + 1] = s.y; seedT[addr * 4 + 2] = code;
    }
    this._overflow = overflow;
    this._tex.pos.needsUpdate = true; this._tex.col.needsUpdate = true; this._tex.seed.needsUpdate = true;
    const u = this._u;
    u.uGrid.value.set(cols, rows);
    u.uBox.value.set(this._x0, this._y0, cw, ch);
    u.uPeriod.value = this._warpMode === WARP_POLAR ? this._bw : 0;
  }

  // --- palette ----------------------------------------------------------------------
  _applyPalette(profile) {
    const pal = profile && profile.artworkPalette;
    if (profile) { if (pal === this._paletteSeen && this._paletteHex) return; this._paletteSeen = pal; }
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors.slice(0, 3) : ACCENT_FALLBACK;
    const h0 = hueOf(v[0]); const hexes = [v[0]];
    for (let k = 1; k < 3; k++) {
      const cand = v[k];
      const ok = cand != null && hexes.every((hx) => hueDist(hueOf(hx), hueOf(cand)) >= 0.25);
      hexes.push(ok ? cand : new THREE.Color().setHSL((h0 + k / 3) % 1, 1, 0.5).getHex());
    }
    this._paletteHex = hexes;
    this._writePalette();
  }
  _writePalette() {
    if (!this._paletteHex) return;
    const shift = (this._palette % 8) * 0.125;
    const h = this._paletteHex.map((hx) => hueOf(vdjColor(hx).getHex()));
    this._u.uHue.value.set((h[0] + shift) % 1, (h[1] + shift) % 1, (h[2] + shift) % 1);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  // --- rhythm -----------------------------------------------------------------------
  _onBeat() {
    this._beatCount++;
    this._pulse = 1;
    const N = this._seeds.length, tgt = this._target();
    let k = this._forceDivide ? 8 : Math.max(3, Math.min(8, Math.round(3 + (tgt - N) / 6 + 4 * this._build)));
    if (this._time < this._reorgUntil) k = 0;
    this._divide(k);
  }
  _onBar() {
    this._barCount++;
    const N = this._seeds.length, tgt = this._target();
    let m = this._forceDivide ? 16 : Math.max(0, Math.min(24, Math.round((N - tgt) / 2 + 6 - 4 * this._build)));
    // after a drop: back to the target in two bars - half the excess on the
    // first, the rest on the second
    if (this._reorgBars > 0) { m = Math.max(m, this._reorgBars >= 2 ? Math.ceil((N - tgt) / 2) : N - tgt); this._reorgBars--; }
    this._merge(m);
  }
  _onPhrase() {
    this._phraseCount++;
    if (this._forceWarp == null) {
      const was = this._warpMode;
      this._warpMode = (this._warpMode + 1) % WARPS.length;
      if ((was === WARP_POLAR) !== (this._warpMode === WARP_POLAR)) for (const s of this._seeds) s.x = Math.max(this._x0, Math.min(this._x0 + this._bw - 1e-4, s.x));
    }
    this._palette++; this._writePalette();
    this._mirror = this._forceMirror != null ? this._forceMirror : (this._phraseCount % 2);
  }
  _onDrop(director) {
    // every cell divides at once, a flash, then two bars to reorganise
    this._divide(this._seeds.length);
    if (director && this._comfort.requestFlash(this._time)) director.flash = Math.max(director.flash || 0, VDJ_DROP_FLASH);
    this._reorgUntil = this._time + 8 * this._beatPeriod;
    this._reorgBars = 2;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const dt = Math.min(deltaTime, 0.05);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    this._beatPeriod = 60 / bpm;
    this._applyPalette(profile);
    const a = safeZoneUniforms.uAspect.value || 16 / 9;
    if (Math.abs(a - this._aspect) > 1e-3) { this._aspect = a; this._setBox(a); }
    if (paused) { this._upload(); return; }        // the tissue holds
    this._time += dt;
    const energy = director && director.intensity != null ? clamp01(director.intensity) : 0.6;
    this._energy += (energy - this._energy) * Math.min(1, dt / 2.0);

    // the beat clock
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true;
      const bi = profile.beatIndex;
      if (bi != null && this._lastBeatIndex != null) this._beats += Math.max(0, (bi - this._lastBeatIndex) + (beatPhase - this._lastBeatPhase));
      else this._beats += dt / this._beatPeriod;
      this._lastBeatIndex = bi != null ? bi : null;
      this._lastBeatPhase = beatPhase;
    } else {
      this._free += dt;
      if (this._free >= this._beatPeriod) { this._free -= this._beatPeriod; beatEdge = true; }
      this._beats += dt / this._beatPeriod;
    }
    // the build: a drop announced within 8 bars
    const ev = director && director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const build = beatsToDrop != null && beatsToDrop < 32 ? clamp01((32 - beatsToDrop) / 28) : (director && director.mode === 'build' ? 0.5 : 0);
    this._build += (build - this._build) * Math.min(1, dt / 0.5);

    if (beatEdge) this._onBeat();
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar(); this._lastBarPhase = barPhase; }
    else if (beatEdge && this._beatCount % 4 === 1 && this._beatCount > 1) this._onBar();
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase(); this._lastPhrasePhase = phrasePhase; }
    else if (beatEdge && this._beatCount % 32 === 1 && this._beatCount > 1) this._onPhrase();
    // the drop: on the frame that crosses the mark (announced), or a timeline mark
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    let drop = false;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey) { this._lastDropKey = key; drop = true; }
    } else if (beatPhase != null && this._tickDrops(profile)) drop = true;
    if (drop) this._onDrop(director);

    this._step(dt);
    this._upload();

    // look
    this._pulse = Math.max(0, this._pulse - dt / (this._beatPeriod * 0.35));
    this._flow += dt * (0.025 + 0.05 * this._build);              // the colours flow
    this._spin += dt * (0.10 + 0.25 * this._build);              // the polar sectors turn
    const bp = barPhase != null ? barPhase : (this._beats % 4) / 4;
    this._kick = vdjCameraKick(bp, this._barCount);
    this._post.update(vdjBeatChroma(beatPhase != null ? beatPhase : this._beats % 1, this._beatPeriod));
    const u = this._u;
    u.uRoll.value = this._kick.roll; u.uKickZoom.value = this._kick.zoom;
    u.uRollCS.value.set(Math.cos(this._kick.roll), Math.sin(this._kick.roll), 1 / this._kick.zoom);
    u.uWedges.value = this._mirror ? 6 : 0;
    u.uWarp.value = this._forceWarp != null ? this._forceWarp : this._warpMode;
    u.uSpin.value = this._spin;
    u.uWarpT.value.set(this._time * 0.6, this._time * 0.47);
    u.uFlow.value = this._flow;
    u.uPulse.value = this._pulse;
    // comfort: a real measure twice a second
    this._lumAge += dt;
    if (this._lumAge > 0.5) { this._lumAge = 0; this._lumPending = true; }
    // the governor is fed ONLY when a measure arrives, with the time it
    // covers: fed every frame with a stale value it cut the gain by 0.7 thirty
    // times before the next measure (exposure 0.005, measured)
    if (this._lumFresh) { this._lumFresh = false; u.uExposure.value = this._comfort.measure(this._lumMeasured, this._lumSince || 0.5); this._lumSince = 0; }
    this._lumSince = (this._lumSince || 0) + dt;
  }

  _beforeRender(renderer) {
    const t = renderer.getRenderTarget();
    let w, h;
    if (t) { w = t.width; h = t.height; }
    else { const v = renderer.getDrawingBufferSize(this._bufSize || (this._bufSize = new THREE.Vector2())); w = v.x; h = v.y; }
    this._u.uPx.value = 1 / Math.max(1, h);
    {
      // the search, before the frame, at a fraction of the frame's size (the
      // composer's target when there is one)
      const sw = Math.max(1, Math.ceil(w / this._layout.search)), sh = Math.max(1, Math.ceil(h / this._layout.search));
      if (this._ids.width !== sw || this._ids.height !== sh) this._ids.setSize(sw, sh);
      this._u.uSearchDiv.value = w / sw;
      renderer.setRenderTarget(this._ids);
      renderer.render(this._searchScene, this._ortho);
      renderer.setRenderTarget(t);
    }
    if (this._lumPending) {
      this._lumPending = false;
      renderer.setRenderTarget(this._lumTarget);
      this._u.uDeriv.value = LUM_H / Math.max(1, h);
      renderer.render(this._lumScene, this._ortho);
      this._u.uDeriv.value = 1;
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_W, LUM_H, this._lumPixels);
      renderer.setRenderTarget(t);
      let sum = 0;
      const px = this._lumPixels;
      for (let i = 0; i < px.length; i += 4) {
        const l = (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) / 255;
        sum += Math.pow(l, 1 / 2.2);
      }
      this._lumMeasured = sum / (LUM_W * LUM_H);
      this._lumFresh = true;
    }
  }

  get probe() {
    const S = this._seeds;
    return {
      t: +this._time.toFixed(2), beats: +this._beats.toFixed(2), count: S.length, target: this._target(),
      dividing: S.filter((s) => s.kind === 1).length, merging: S.filter((s) => s.kind === 2).length,
      warp: WARPS[this._u.uWarp.value], mirror: this._mirror, energy: +this._energy.toFixed(2), build: +this._build.toFixed(2),
      grid: [this._u.uGrid.value.x, this._u.uGrid.value.y], overflow: this._overflow, nnCV: this._nnCV(), exposure: +this._u.uExposure.value.toFixed(3), lum: +this._lumMeasured.toFixed(3)
    };
  }

  // regularity: the coefficient of variation of the nearest-neighbour distances
  _nnCV() {
    const S = this._seeds, n = S.length, v = [];
    for (let i = 0; i < n; i++) { let m = 1e9; for (let j = 0; j < n; j++) if (j !== i) m = Math.min(m, Math.hypot(S[j].x - S[i].x, S[j].y - S[i].y)); v.push(m); }
    const mean = v.reduce((a, b) => a + b, 0) / n, sd = Math.sqrt(v.reduce((a, b) => a + (b - mean) * (b - mean), 0) / n);
    return +(sd / mean).toFixed(2);
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); this._display.geometry.dispose(); }
    if (this._lumQuad) this._lumQuad.geometry.dispose();
    if (this._searchQuad) this._searchQuad.geometry.dispose();
    if (this._ids) { this._ids.dispose(); this._ids = null; }
    if (this._lumTarget) { this._lumTarget.dispose(); this._lumTarget = null; }
    this._scene = null;
  }
}
